import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/shared/components/ui/accordion";
import { CheckCircle, ArrowRight, ChevronDown } from "lucide-react";
import { SEO } from "@/shared/components/SEO";
import { trackViewContent, trackSchedule } from "@/shared/utils/metaPixel";

export default function PricingPage() {
  const navigate = useNavigate();
  const [numberOfUsers, setNumberOfUsers] = useState("2-included");
  const [videoStorage, setVideoStorage] = useState("30-days-included");
  const [invoicing, setInvoicing] = useState("no-invoicing");

  // Track ViewContent for Meta Pixel on page load
  useEffect(() => {
    trackViewContent({
      contentName: 'Pricing Page',
      contentCategory: 'Pricing',
    });
  }, []);

  const handleScheduleDemo = () => {
    // Track Schedule event for Meta Pixel (demo scheduling intent)
    trackSchedule({
      email: '', // Anonymous user
    }, 'Demo Request');
    window.open('https://api.leadconnectorhq.com/widget/booking/9yoGqzJkpywxCy012LYn', '_blank');
  };

  const calculatePrice = () => {
    let basePrice = 99;

    // Add user pricing (base includes 2 users now)
    switch (numberOfUsers) {
      case "3-user": basePrice += 50; break;  // 1 extra user
      case "4-user": basePrice += 100; break; // 2 extra users
      case "5-user": basePrice += 150; break; // 3 extra users
      case "6-user": basePrice += 200; break; // 4 extra users
    }

    // Add storage pricing
    switch (videoStorage) {
      case "90-days": basePrice += 7.50; break;
      case "1-year": basePrice += 5; break;
    }

    // Add invoicing pricing
    if (invoicing === "with-invoicing") {
      basePrice += 15;
    }

    return basePrice;
  };

  const getPlanTitle = () => {
    return numberOfUsers === "2-included" ? "wlkthru pro" : "wlkthru team";
  };

  const isEligibleForTrial = () => {
    return numberOfUsers === "2-included" && videoStorage === "30-days-included";
  };

  const handleGetStarted = () => {
    if (isEligibleForTrial()) {
      // Navigate to auth for trial signup
      navigate('/auth?mode=signup&trial=true', { state: { from: '/pricing' } });
    } else {
      // Navigate directly to billing for paid plans
      navigate('/profile?tab=billing');
    }
  };

  const handleContactSales = () => {
    navigate('/contact');
  };

  return (
    <>
      <SEO
        title="Pricing - Wlkthru Video Calling Platform"
        description="Transparent pricing for wlkthru.co video calling platform. Start with a free 14-day trial. Plans for contractors, inspectors, and service professionals with flexible user options."
        canonical="https://www.wlkthru.co/pricing"
        keywords="wlkthru pricing, wlkthru.co cost, video calling pricing, contractor software pricing, professional video platform cost"
      />
      <div className="min-h-screen bg-background">
        {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-b border-border z-50">
        <div className="container mx-auto px-6 py-2">
          <div className="flex items-center justify-between">
            {/* Left side navigation */}
            <nav className="flex items-center space-x-8 ml-1 md:ml-0">
              <button 
                onClick={() => {
                  navigate('/');
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                }}
                className="text-xs md:text-2xl font-bold text-primary hover:text-primary/90 transition-colors"
              >
                wlkthru
              </button>
              <div className="hidden md:flex space-x-8">
                <button
                  onClick={() => {
                    navigate('/features');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="text-sm font-medium transition-colors hover:text-primary text-foreground"
                >
                  Features
                </button>
                <button
                  onClick={() => {
                    navigate('/pricing');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="text-sm font-medium text-primary"
                >
                  Pricing
                </button>
              </div>
              {/* Mobile navigation - next to logo */}
              <div className="md:hidden flex space-x-3">
                <button
                  onClick={() => {
                    navigate('/features');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="text-xs font-medium transition-colors hover:text-primary text-foreground"
                >
                  Features
                </button>
                <button
                  onClick={() => {
                    navigate('/pricing');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="text-xs font-medium text-primary"
                >
                  Pricing
                </button>
              </div>
            </nav>

            {/* Mobile Navigation */}
            <div className="md:hidden flex items-center">
              <button 
                onClick={() => navigate('/auth')}
                className="text-xs text-muted-foreground px-2 py-1.5 hover:text-foreground transition-colors cursor-pointer"
              >
                Log in
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <button 
                onClick={() => navigate('/auth')}
                className="text-sm text-muted-foreground px-3 py-2 hover:text-foreground transition-colors cursor-pointer"
              >
                Log in
              </button>
              <button 
                onClick={handleGetStarted}
                className="text-sm px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors cursor-pointer"
              >
                Get started for free
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-background relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-primary/10 rounded-full opacity-50"></div>
        <div className="absolute bottom-10 right-40 w-16 h-16 bg-primary/20 rounded-full opacity-30"></div>
        <div className="absolute top-40 right-10 w-8 h-8 bg-primary/30 rounded-full opacity-40"></div>
        
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left side - Text and buttons */}
            <div className="space-y-8">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight animate-slide-in-right">
                <span className="text-primary">15-day Free Trial.</span>
              </h1>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  variant="outline"
                  size="lg"
                  className="px-8 py-6 text-lg"
                  onClick={handleScheduleDemo}
                >
                  Schedule Demo
                </Button>
              </div>
            </div>

            {/* Right side - Dashboard Preview */}
            <div className="relative flex items-center justify-center">
              <div className="w-full max-w-3xl rounded-lg overflow-hidden shadow-2xl">
                <img
                  src="/Pricing Image .png"
                  alt="wlkthru pricing dashboard"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pb-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="flex justify-center max-w-6xl mx-auto">
            {/* Professional Plan - Centered */}
            <Card className="relative border-2 border-primary hover:shadow-lg transition-shadow w-full max-w-md mx-auto">
              <CardHeader className="text-center pt-10 pb-8">
                <CardTitle className="text-3xl mb-2">{getPlanTitle().replace('pro', 'Pro').replace('team', 'Team')}</CardTitle>
                 <div className="mt-6 mb-8">
                   <span className="text-5xl font-bold">${calculatePrice()}</span>
                   <span className="text-muted-foreground text-lg">/month</span>
                 </div>
                
                {/* Pricing Dropdowns */}
                <div className="space-y-4 text-left">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Number of Users</label>
                    <Select value={numberOfUsers} onValueChange={setNumberOfUsers}>
                      <SelectTrigger className="w-full bg-background border border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-background border border-border shadow-lg z-50">
                        <SelectItem value="2-included">2 users (admins) - included</SelectItem>
                        <SelectItem value="3-user">3 users (+$50/month)</SelectItem>
                        <SelectItem value="4-user">4 users (+$100/month)</SelectItem>
                        <SelectItem value="5-user">5 users (+$150/month)</SelectItem>
                        <SelectItem value="6-user">6 users (+$200/month)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Video Storage</label>
                    <Select value={videoStorage} onValueChange={setVideoStorage}>
                      <SelectTrigger className="w-full bg-background border border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-background border border-border shadow-lg z-50">
                        <SelectItem value="30-days-included">30 days of storage (included)</SelectItem>
                        <SelectItem value="90-days">90 days (+$7.50/month)</SelectItem>
                        <SelectItem value="1-year">1 year (+$5.00/month)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Invoicing</label>
                    <Select value={invoicing} onValueChange={setInvoicing}>
                      <SelectTrigger className="w-full bg-background border border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-background border border-border shadow-lg z-50">
                        <SelectItem value="no-invoicing">Not included</SelectItem>
                        <SelectItem value="with-invoicing">Invoicing (+$15/month, $15 one-time setup)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 px-8 pb-10">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-sm">Unlimited Video Calls</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-sm">SMS Integration</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-sm">Customer Profiles</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-sm">AI Transcript</span>
                  </div>
                 </div>
                 
                 {/* Trial eligibility notice */}
                 {!isEligibleForTrial() && (
                   <div className="text-xs text-muted-foreground text-center mb-4">
                     Free 15-day trial only valid for 2 users (admins) and 30 days of storage
                   </div>
                 )}
                 
                 <Button onClick={handleGetStarted} variant="cta-primary" className="w-full mt-8 py-2 text-sm">
                   {isEligibleForTrial() ? "Start free trial" : "Sign up now"}
                 </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-foreground mb-16">
              Frequently asked questions
            </h2>
            <Accordion type="single" collapsible className="w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <AccordionItem value="item-1" className="border border-border rounded-lg bg-background">
                    <AccordionTrigger className="text-lg font-semibold px-4 hover:no-underline text-left">
                      Who is wlkthru for?
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 text-muted-foreground text-left">
                      We're built for trades pros — movers, repair techs, HVAC, plumbing, appliance, handyman and more — anyone who needs to see the problem to solve it faster.
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="item-2" className="border border-border rounded-lg bg-background">
                    <AccordionTrigger className="text-lg font-semibold px-4 hover:no-underline text-left">
                      How does a customer join a video call?
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 text-muted-foreground text-left">
                      Just send them a link — no downloads or logins required. They can join directly from their phone or computer.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-3" className="border border-border rounded-lg bg-background">
                    <AccordionTrigger className="text-lg font-semibold px-4 hover:no-underline text-left">
                      Is wlkthru mobile-friendly?
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 text-muted-foreground text-left">
                      Absolutely — wlkthru works seamlessly on phones, tablets, and desktops.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-4" className="border border-border rounded-lg bg-background">
                    <AccordionTrigger className="text-lg font-semibold px-4 hover:no-underline text-left">
                      Can I cancel anytime?
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 text-muted-foreground text-left">
                      Yes, you can cancel your subscription at any time. No long-term contracts or cancellation fees.
                    </AccordionContent>
                  </AccordionItem>
                </div>
                
                <div className="space-y-4">
                  <AccordionItem value="item-5" className="border border-border rounded-lg bg-background">
                    <AccordionTrigger className="text-lg font-semibold px-4 hover:no-underline text-left">
                      Do I need to install anything to use it?
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 text-muted-foreground text-left">
                      Nope — it's all browser-based. Just log in and start working.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-6" className="border border-border rounded-lg bg-background">
                    <AccordionTrigger className="text-lg font-semibold px-4 hover:no-underline text-left">
                      Does wlkthru integrate with other tools?
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 text-muted-foreground text-left">
                      We're working on integrations with platforms like Jobber, ServiceTitan, Housecall Pro and others — stay tuned or contact us to request one.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-7" className="border border-border rounded-lg bg-background">
                    <AccordionTrigger className="text-lg font-semibold px-4 hover:no-underline text-left">
                      How does the free trial work?
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 text-muted-foreground text-left">
                      You get full access free for 15 days. We'll send you a reminder before your trial ends so there are no surprises.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-8" className="border border-border rounded-lg bg-background">
                    <AccordionTrigger className="text-lg font-semibold px-4 hover:no-underline text-left">
                      What happens to the videos after they're recorded?
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 text-muted-foreground text-left">
                      You have the option to save the videos which will be securely stored in your account so you can view, download, or reference them anytime.
                    </AccordionContent>
                  </AccordionItem>
                </div>
              </div>
            </Accordion>
          </div>
        </div>
      </section>


      {/* Footer */}
      <footer className="py-12 pb-6 bg-primary text-center md:text-left">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-white">
            {/* Brand Section */}
            <div className="space-y-4">
              <button 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-4xl font-bold hover:text-white/80 transition-colors cursor-pointer"
              >
                wlkthru
              </button>
              <div className="w-16 h-0.5 bg-white/30 mx-auto md:mx-0"></div>
              <div className="space-y-3">
                <p className="text-white/90">Check us out on:</p>
                <div className="flex gap-2 justify-center">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61578600642587" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">f</span>
                  </a>
                  <a 
                    href="https://www.instagram.com/wlkthru.co/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">ig</span>
                  </a>
                  <a 
                    href="https://www.linkedin.com/company/wlkthruco/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">in</span>
                  </a>
                  <a 
                    href="https://www.youtube.com/@wlkthruco" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">yt</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Get in Touch and Legal */}
            <div className="flex flex-col md:flex-row gap-8 md:col-span-2 justify-end">
              {/* Get in Touch */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white">Get in Touch</h4>
                <ul className="space-y-2">
                  <li><a href="/customer-support" className="text-white/80 hover:text-white transition-colors">• Customer Support</a></li>
                  <li><a href="https://api.leadconnectorhq.com/widget/booking/9yoGqzJkpywxCy012LYn" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors">• Demo Request</a></li>
                </ul>
              </div>

              {/* Legal */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white">Legal</h4>
                <ul className="space-y-2">
                  <li><button onClick={() => { navigate('/legal?section=privacy'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="text-white/80 hover:text-white transition-colors text-left">• Privacy Policy</button></li>
                  <li><button onClick={() => { navigate('/legal?section=terms'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="text-white/80 hover:text-white transition-colors text-left">• Terms and Conditions</button></li>
                  <li><button onClick={() => { navigate('/legal?section=disclaimer'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="text-white/80 hover:text-white transition-colors text-left">• Disclaimer</button></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-4 pt-2 border-t border-white/20 text-center">
            <p className="text-white/60 text-xs font-light">
              Copyright © 2025 wlkthru | All rights reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}